/**
 * playlistCacheLru — v1.3.76 in-memory LRU shim over the existing
 * IndexedDB cache layer in `playlistCache.js`.
 *
 * Why
 * ---
 * IDB reads are ~10-30 ms on a Smart TV WebView. That's fine for a
 * cold tab open, but the user tapping a category, going Back, then
 * tapping it again should feel **instant** (the ITVZA Smart-TV
 * benchmark we're chasing).
 *
 * This module keeps the IDB layer as the source of truth and only
 * caches what was successfully read/written in JS heap. Eviction
 * is strict LRU on access-count; size limits are tuned so the
 * combined heap stays under ~15 MB even for a 200k-channel
 * provider.
 *
 * Drop-in: the public API is just `get`/`set`/`invalidate`/
 * `clearAll` keyed by namespace+args, so the existing
 * `getCachedXxx`/`cacheXxx` functions only need a 2-line patch each.
 */

/**
 * Single-namespace LRU map. Keeps insertion order in a JS Map
 * (which preserves insertion order per spec) and re-inserts on
 * access so the oldest entry is always at the head.
 *
 * Each value is wrapped { v, t } where `t` is the epoch at which
 * the IDB layer already verified its TTL — we use it as a final
 * safety net to evict entries that have outlived the IDB TTL even
 * if `invalidate()` was missed somewhere.
 */
class NamespaceLru {
  constructor(name, maxEntries, ttlMs) {
    this.name = name;
    this.maxEntries = maxEntries;
    this.ttlMs = ttlMs;
    this.map = new Map();
    this._hits = 0;
    this._misses = 0;
  }

  get(key) {
    const entry = this.map.get(key);
    if (!entry) {
      this._misses += 1;
      return undefined;
    }
    if (this.ttlMs && Date.now() - entry.t > this.ttlMs) {
      // Expired — drop it so the IDB layer re-reads.
      this.map.delete(key);
      this._misses += 1;
      return undefined;
    }
    // Re-insert to mark as most-recently-used.
    this.map.delete(key);
    this.map.set(key, entry);
    this._hits += 1;
    return entry.v;
  }

  set(key, value) {
    if (value == null) return;
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, { v: value, t: Date.now() });
    while (this.map.size > this.maxEntries) {
      // First key is the oldest (least-recently used).
      const oldest = this.map.keys().next().value;
      if (oldest === undefined) break;
      this.map.delete(oldest);
    }
  }

  invalidate(key) {
    this.map.delete(key);
  }

  clear() {
    this.map.clear();
    // Reset diagnostic counters too — `clearAll` is also the
    // documented way for tests to get a clean slate.
    this._hits = 0;
    this._misses = 0;
  }

  stats() {
    return {
      name: this.name,
      size: this.map.size,
      max: this.maxEntries,
      hits: this._hits,
      misses: this._misses,
    };
  }
}

// =====================================================================
// Per-namespace caches. Sizes tuned for the worst-case TV WebView heap.
// =====================================================================
//
// `vod_streams` and `chan_by_group` are the heavy hitters — per-entry
// payload can be 100-500 KB on huge providers, so the entry counts
// are deliberately small. Categories and short EPG entries are tiny
// so we can keep hundreds in memory cheaply.
//
// TTLs mirror the IDB TTLs in playlistCache.js — the LRU never holds
// data longer than the persistent store does. Slightly shorter so a
// race-condition write that bypasses the LRU still gets corrected
// within a single IDB TTL cycle.
const _LRU = {
  series_eps:        new NamespaceLru("series_eps",     150,  25 * 60 * 1000), // ~30m IDB TTL
  vod_cats:          new NamespaceLru("vod_cats",        8,   23 * 60 * 60 * 1000),
  vod_streams:       new NamespaceLru("vod_streams",    20,   55 * 60 * 1000),
  live_cats:         new NamespaceLru("live_cats",       8,   23 * 60 * 60 * 1000),
  live_streams:      new NamespaceLru("live_streams",   50,   14 * 60 * 1000),
  short_epg:         new NamespaceLru("short_epg",     500,    4 * 60 * 1000),
  chan_by_group:     new NamespaceLru("chan_by_group",  20,   60 * 60 * 1000),
  cats_for_type:     new NamespaceLru("cats_for_type",  16,   60 * 60 * 1000),
};

/**
 * Build a stable string key from any number of args. Coerces null/
 * undefined to empty so different falsy values don't collide.
 */
function buildKey(args) {
  return args.map((a) => (a == null ? "" : String(a))).join("|");
}

/**
 * Public API — `ns` must be one of the keys in `_LRU` above.
 */
export function lruGet(ns, ...args) {
  const cache = _LRU[ns];
  if (!cache) return undefined;
  return cache.get(buildKey(args));
}

export function lruSet(ns, args, value) {
  const cache = _LRU[ns];
  if (!cache) return;
  cache.set(buildKey(args), value);
}

export function lruInvalidate(ns, ...args) {
  const cache = _LRU[ns];
  if (!cache) return;
  cache.invalidate(buildKey(args));
}

export function lruClearNamespace(ns) {
  const cache = _LRU[ns];
  if (cache) cache.clear();
}

/** Clear everything — used by `clearAllPlaylistCaches`. */
export function lruClearAll() {
  Object.values(_LRU).forEach((c) => c.clear());
}

/**
 * Diagnostic — exposed on `window.__zapp_lru_stats__` so we can
 * spot-check hit ratios from devtools without reaching into module
 * scope.
 */
export function lruStats() {
  return Object.values(_LRU).map((c) => c.stats());
}

if (typeof window !== "undefined") {
  window.__zapp_lru_stats__ = lruStats;
}
